import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Image,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  Icon,
  HStack,
  VStack,
  useDisclosure,
  Avatar,
  Text,
  Collapse,
} from "@chakra-ui/react";
import { AiOutlineMenu } from "react-icons/ai";
import { FaFilter } from "react-icons/fa"
import Searchbar from "./Searchbar";
import logoIcon from "../assets/Leaderbored_logo_HD.webp";
import { getAuthToken } from "../utilities/auth";
import loginIcon from "../assets/Nav-Icons/LBPortal_Graphic_Login.png";

const GAME_CATEGORIES_LINKS = [
  {
    path: "/g/combat",
    label: 'Combat',
    // icon: GiSpinningSword
  },
  {
    path: "/g/cooking",
    label: 'Cooking',
    // icon: GiPieSlice
  },
  {
    path: "/g/hyper-casual",
    label: 'Hyper-casual',
    // icon: GiWineGlass
  },
  {
    path: "/g/multiplayer",
    label: 'Multiplayer',
    // icon: GiCrossedAxes
  },
  {
    path: "/g/music",
    label: 'Music',
    // icon: GiGuitarBassHead
  },
  {
    path: "/g/platformer",
    label: 'Platformer',
    // icon: GiLaddersPlatform
  },
  {
    path: "/g/puzzle",
    label: 'Puzzle',
    // icon: GiJigsawPiece
  },
  {
    path: "/g/rhythm",
    label: 'Rhythm',
    // icon: GiBoulderDash
  },
  {
    path: "/g/shooter",
    label: 'Shooter',
    // icon: GiBolterGun
  },
  {
    path: "/g/side-scroller",
    label: 'Side-scroller',
    // icon: GiJumpAcross
  },
  {
    path: "/g/sorting",
    label: 'Sorting',
    // icon: FaGamepad
  },
  {
    path: "/g/sports",
    label: 'Sports',
    // icon: GiBasketballBall
  },
  {
    path: "/g/vertical-scroller",
    label: 'Vertical Scroller',
    // icon: GiFallDown
  },
]

function MobileNav({ navItems, socials }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [showCategories, setShowCategories] = useState(false);
  const token = getAuthToken();

  const handleToggleCategories = () => setShowCategories(!showCategories);

  const renderNavItems = navItems.map((item) => (
    <Box
      onClick={onClose}
      borderRadius='10px'
      as={Link}
      key={item.text}
      to={item.path}
      w="100%"
    >
      <HStack p='4px' gap='8px' justifyContent='end'>
        <Box flex='1' textAlign="right">{item.text}</Box>
        <Box w='35px' h='35px' flexShrink={0}>
          <Image loading='lazy' src={item.icon} alt='Navigation icon' />
        </Box>
      </HStack>
    </Box>
  ));

  return (
    <>
      <HStack w='100%' mx='16px' justifyContent='space-between'>
        <Icon fontSize='40px' as={AiOutlineMenu} onClick={onOpen} cursor="pointer" />
        <Box as={Link} to='/'>
          <Image src={logoIcon} alt='leaderbored logo' w='72px' />
        </Box>
        <Box boxSize={10}>
          {token && (
            <Link to='/account'>
              <Avatar as='button' bg='primaryLight' boxSize={10} />
            </Link>
          )}
        </Box>
      </HStack>

      <Drawer placement='left' onClose={onClose} isOpen={isOpen}>
        <DrawerOverlay>
          <DrawerContent color='white' bgColor='primary'>
            <DrawerCloseButton />
            <DrawerBody
              marginTop='56px'
              display="flex"
              flexDirection="column"
            >
              <Box marginBottom='32px'>
                <Searchbar width='100%' />
              </Box>

              <VStack gap='16px' alignItems='end' w="100%" flex="1">
                {renderNavItems}

                {/* --- Scrollable Categories Dropdown --- */}
                <Box w="100%">
                  <HStack
                    p='4px'
                    gap='8px'
                    justifyContent='end'
                    onClick={handleToggleCategories}
                    cursor="pointer"
                    _hover={{ opacity: 0.8 }}
                  >
                    <Box flex='1' textAlign="right">Categories</Box>
                    <Box w='35px' h='35px' display="flex" alignItems="center" justifyContent="center">
                      <Icon
                        as={FaFilter}
                        w='32px'
                        h='32px'
                      />
                    </Box>
                  </HStack>

                  <Collapse in={showCategories} animateOpacity>
                    <Box
                      maxH="200px" // Limits the height of the list
                      overflowY="auto" // Enables scrolling
                      mt="8px"
                      pr="10px"
                      mr="17px"
                      borderRight="2px solid"
                      borderColor="whiteAlpha.300"
                      // Custom Scrollbar Styling
                      css={{
                        '&::-webkit-scrollbar': { width: '4px' },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': {
                          background: 'rgba(255, 255, 255, 0.3)',
                          borderRadius: '10px'
                        },
                      }}
                    >
                      <VStack alignItems="end" gap="14px">
                        {GAME_CATEGORIES_LINKS.map((cat) => (
                          <Text
                            key={cat.label}
                            as={Link}
                            state={{ pageTitle: cat.label }}
                            to={`${cat.path.toLowerCase()}`}
                            onClick={onClose}
                            fontSize="sm"
                            whiteSpace="nowrap"
                            _hover={{ color: "primaryLight" }}
                          >
                            {cat.label}
                          </Text>
                        ))}
                      </VStack>
                    </Box>
                  </Collapse>
                </Box>

                {!token && (
                  <HStack onClick={onClose} as={Link} to='/auth' p='4px' gap='8px'>
                    <Text>Login</Text>
                    <Image src={loginIcon} alt='loginIcon' loading='lazy' />
                  </HStack>
                )}
              </VStack>

              {/* Socials pinned at the bottom */}
              <HStack gap='16px' py='24px' alignItems='center' justifyContent='end'>
                {socials.map((social, index) => (
                  <Link key={index} target='_blank' to={social.link}>
                    <Icon as={social.icon} boxSize={8} />
                  </Link>
                ))}
              </HStack>
            </DrawerBody>
          </DrawerContent>
        </DrawerOverlay>
      </Drawer>
    </>
  );
}

export default MobileNav;
